import { Box, Container, CssBaseline, ThemeProvider } from '@mui/material';
import { Typography } from '../../primitives/typography/typography';

import theme from '../../../theme/theme';
import { Button } from '../../primitives/button';

export interface NotFoundPageProps {
  title?: string;
  message?: string;
  returnLink?: string;
  returnLinkLabel?: string;
  wrapWithTheme?: boolean;
}

function NotFoundPageContent({
  title = 'Page Not Found',
  message = "The page you're looking for doesn't exist or could not be found.",
  returnLink = '/',
  returnLinkLabel = 'Go Back Home',
}: Readonly<Omit<NotFoundPageProps, 'wrapWithTheme'>>) {
  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          textAlign: 'center',
          color: 'text.secondary',
          gap: 2,
        }}
      >
        <Typography.Override variant="h1" sx={{ color: 'primary.dark', fontWeight: 'bold' }}>
          404
        </Typography.Override>
        <Typography.Heading>{title}</Typography.Heading>
        <Typography.Body sx={{ mb: 3 }}>{message}</Typography.Body>
        <Button variant="primary" to={returnLink}>
          {returnLinkLabel}
        </Button>
      </Box>
    </Container>
  );
}

export function NotFoundPage({
  title,
  message,
  returnLink,
  returnLinkLabel,
  wrapWithTheme = true,
}: Readonly<NotFoundPageProps>) {
  const content = (
    <NotFoundPageContent
      title={title}
      message={message}
      returnLink={returnLink}
      returnLinkLabel={returnLinkLabel}
    />
  );

  // When used in error boundary (wrapWithTheme=true), provide full HTML structure with theme
  if (wrapWithTheme) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {content}
      </ThemeProvider>
    );
  }

  // When used inside themed layouts, just return content
  return content;
}
